import * as React from 'react';
import './fields.scss';
import { useTranslations } from '../../../store/translations/hooks';
import { getWeekDayTranslationKey } from '../../../shared/utils';
import { Box } from '@mui/material';
import { ComboField } from './ComboField';
import { TK } from '../../../store/translations/translationKeys';
import Typography from '../../Typography';
import { RecurringInfo, RecurrenceFrequency } from '../../../models/api-models';
import { DateField, NumberInputField } from '.';

export interface RecurringEventFieldProps {
    id?: string,
    value: RecurringInfo,
    onChange: (value: RecurringInfo) => void,
    disabled?: boolean,
}

export const RecurringEventField: React.FC<RecurringEventFieldProps> = (props) => {
    const {
        id,
        value,
        onChange,
        disabled,
    } = props;

    const t = useTranslations();

    const toggleWeekDay = (dayIndex: number) => {
        const currentDays = value.weekDays || [];
        const newDays = currentDays.includes(dayIndex)
            ? currentDays.filter(d => d !== dayIndex)
            : [...currentDays, dayIndex].sort((a, b) => a - b);
        onChange({ ...value, weekDays: newDays });
    };

    const weekDays = [1, 2, 3, 4, 5, 6, 0]; // Monday to Sunday
    const frequencyOptions = [
        { value: RecurrenceFrequency.Daily, label: t(TK.daily) },
        { value: RecurrenceFrequency.Weekly, label: t(TK.weekly) },
        { value: RecurrenceFrequency.Monthly, label: t(TK.monthly) },
    ];

    // Map enum value to display label for ComboField options
    const frequencyDisplayOptions = frequencyOptions.map(o => o.label);

    // Get current frequency label
    const currentLabel = frequencyOptions.find(o => o.value === value.frequency)?.label;


    const periodUnits = {
        [t(TK.days)]: RecurrenceFrequency.Daily,
        [t(TK.weeks)]: RecurrenceFrequency.Weekly,
        [t(TK.months)]: RecurrenceFrequency.Monthly,
        [t(TK.day)]: RecurrenceFrequency.Daily,
        [t(TK.week)]: RecurrenceFrequency.Weekly,
        [t(TK.month)]: RecurrenceFrequency.Monthly,
        [RecurrenceFrequency.Daily]: (value.period ?? 0) > 1 ? t(TK.days) : t(TK.day),
        [RecurrenceFrequency.Weekly]: (value.period ?? 0) > 1 ? t(TK.weeks) : t(TK.week),
        [RecurrenceFrequency.Monthly]: (value.period ?? 0) > 1 ? t(TK.months) : t(TK.month),
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
            <Box display="flex" gap={2} flexWrap="wrap">
                <Box maxWidth="300px">
                    <ComboField
                        id={id ? `${id}-frequency` : undefined}
                        label={t(TK.frequency)}
                        disabled={disabled}
                        value={currentLabel}
                        options={frequencyDisplayOptions}
                        onChange={(label) => {
                            const selected = frequencyOptions.find(o => o.label === label);
                            if (selected) {
                                onChange({
                                    ...value,
                                    frequency: selected.value,
                                    weekDays: selected.value === RecurrenceFrequency.Weekly ? (value.weekDays || [1, 2, 3, 4, 5]) : []
                                });
                            }
                        }}
                    />
                </Box>

                <Box maxWidth="180px">
                    <NumberInputField
                        min={1}
                        label={t(TK.period)}
                        value={value.period ?? 1}
                        prepend={t(TK.every)}
                        append={periodUnits[value.frequency]}
                        onChange={period => onChange({...value, period: period ?? 1})}
                    />
                </Box>

                {value.frequency === RecurrenceFrequency.Weekly &&
                    <div style={{position: 'relative', top: '-10px', height: '35px'}}>
                        <Typography size="smaller" >{t(TK.weekDays)}</Typography>
                        <div style={{ display: 'flex', gap: 'var(--spacer-XS)'}}>
                            {weekDays.map(dayIndex => {
                                const isSelected = value.weekDays?.includes(dayIndex);
                                const dayName = t(getWeekDayTranslationKey(dayIndex));
                                const firstLetter = dayName.charAt(0).toUpperCase();

                                return (
                                    <button
                                        key={dayIndex}
                                        id={id ? `${id}-weekday-${dayIndex}` : undefined}
                                        type="button"
                                        disabled={disabled}
                                        onClick={() => toggleWeekDay(dayIndex)}
                                        style={{
                                            width: '21px',
                                            height: '21px',
                                            borderRadius: '50%',
                                            border: '2px solid var(--gray, #007bff)',
                                            backgroundColor: isSelected ? 'var(--gray, #007bff)' : 'transparent',
                                            color: isSelected ? 'white' : 'var(--gray, #007bff)',
                                            cursor: disabled ? 'not-allowed' : 'pointer',
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 0,
                                            transition: 'all 0.2s ease',
                                            opacity: disabled ? 0.5 : 1,
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!disabled && !isSelected) {
                                                e.currentTarget.style.backgroundColor = 'rgba(0, 123, 255, 0.1)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }
                                        }}
                                    >
                                        {firstLetter}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                }
            </Box>
            <Box maxWidth="300px">
                <DateField
                    id={id ? `${id}-end-date` : undefined}
                    label={t(TK.endDate)}
                    disabled={disabled}
                    type="date"
                    value={value.endDate}
                    onChange={endDate => onChange({ ...value, endDate })}
                />
            </Box>
        </div>
    );
}
